import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Link, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { AffiliateOrganization } from "schemas/dashboard";

import {
  AFFILIATE_TERMS_AND_CONDITIONS_LINK,
  COMMISSION_PERCENTAGE,
} from "constants/influencer";
import { fetcherAuth } from "utils/api";

import AffiliatePartnerView from "./AffiliatePartnerView";
import SpecialBonuses from "./SpecialBonuses";

export default function AffiliateProgram() {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [affiliateOrg, setAffiliateOrg] =
    useState<AffiliateOrganization | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchAffiliateOrg = async () => {
    if (!currentOrg?.id) return;
    try {
      setLoading(true);
      const { affiliateOrg } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations`,
        "GET",
      );
      setAffiliateOrg(affiliateOrg);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const createAffiliateOrganization = async () => {
    try {
      setLoading(true);
      const { affiliateOrg } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/affiliate-organizations`,
        "POST",
      );
      setAffiliateOrg(affiliateOrg);
      setAlert("Successfully opted in to affiliate program", "success");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      fetchAffiliateOrg();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentOrg?.id],
  );

  if (loading) {
    return (
      <Box>
        <Skeleton height={40} />
        <Skeleton height={80} />
        <Skeleton height={40} />
      </Box>
    );
  }

  if (affiliateOrg?.isActive) {
    return <AffiliatePartnerView affiliateOrg={affiliateOrg} />;
  }

  const perks = [
    {
      text: <>{COMMISSION_PERCENTAGE}% commission on every referral</>,
    },
    {
      text: <>Special discount codes to share (requires approval)</>,
    },
    {
      text: <>Exclusive prizes for top performers</>,
    },
    {
      text: (
        <>
          A chance to <strong>earn additional income</strong> by licensing your
          content about Bento for use in ads
        </>
      ),
    },
  ];

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Join the Bento Affiliate Program
      </Typography>

      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Earn commission on every referral—starting as soon as you opt in! As a
        Bento affiliate, you'll enjoy exclusive perks.{" "}
        <Link
          sx={{ textDecoration: "none", fontWeight: 600, color: "#f4f5f7" }}
          target="_blank"
          href={AFFILIATE_TERMS_AND_CONDITIONS_LINK}
        >
          T&Cs apply
        </Link>
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 3,
          maxWidth: 520,
        }}
      >
        {perks.map((perk, i) => (
          <Box
            key={i}
            sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#01c850",
                flexShrink: 0,
                mt: "6px",
              }}
            />
            <Typography>{perk.text}</Typography>
          </Box>
        ))}
      </Box>

      <SpecialBonuses />

      <Button
        disableElevation
        variant="contained"
        onClick={createAffiliateOrganization}
      >
        Opt In
      </Button>
    </>
  );
}
